import { Fragment } from "react"
import {
    Divider,
    Checkbox,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogContent,
    CircularProgress
} from "@mui/material"
import { ExpandMore, FiberManualRecord } from '@mui/icons-material'
import useCheckout from "./hooks/useCheckout"
import AlertDialog from "../../../components/Dialog/AlertDialog"
import { ccyFormat } from "../../../utils/currencyFormatter"
import { H6, Body3, Body1, Body2, H5 } from "../../../themes/typography"

/**
 * demo example ids ->171, 166, 169, 149 to check the cart calculation
 */

export default function Checkout() {

    const {
        orderSummary,
        showTaxBreakup,
        setShowTaxBreakup,
        phonepeUrl,
        loading,
        openIframe,
        setOpenIFrame,
        initiatePaymentProcess,
        openModal,
        modalVariant,
        goToEventPage,
        onModalContinue
    } = useCheckout();

    return (
        <Fragment>
            <div className="event-ticket-purchase-left-container">

                <div className="event-ticket-purchase-summary">

                    <div className="event-ticket-buyer-details-container">
                        <H5>
                            Billing Detail
                        </H5>

                        <div className="event-ticket-buyer-details">
                            <div className="buyer-user-details">
                                <Body1 fontWeight={500} color="var(--secondary-color)">
                                    Prashant Chaute
                                </Body1>
                                <div className="buyer-user-contact-details">
                                    <Body2>
                                        8128033899
                                    </Body2>
                                    <Body3>|</Body3>
                                    <Body2>
                                        prashant.chaute@example.com
                                    </Body2>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="event-purchased-ticket-list-container">
                        <H5>
                            Purchased Tickets Summary
                        </H5>

                        <div className="event-purchased-ticket-list">

                            {orderSummary?.ticketSummary.map((ticket, index) => (
                                <Fragment>
                                    <div key={ticket.ticketTypeId} className="event-purchased-ticket-item">
                                        <div className="purchased-ticket-title-desc">
                                            <H6 color="var(--secondary-color)" fontWeight={700}>
                                                {ticket.ticketName} x {ticket.tickets.filter(t => t.parentId === null).length}
                                            </H6>
                                        </div>
                                        <div className="purchased-ticket-attendees-list">
                                            {ticket.tickets.map((attendee) => (
                                                <div key={attendee.purchaseTicketId} className="purchased-ticket-attendee-item">
                                                    <FiberManualRecord sx={{ fontSize: '0.5rem', color: 'var(--quaternary-color)' }} />
                                                    <Body2>
                                                        {attendee.attendeeName}
                                                    </Body2>
                                                    <Body2>
                                                        {attendee.attendeePhone}
                                                    </Body2>
                                                    <Body2>
                                                        {attendee.attendeeEmail}
                                                    </Body2>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {index < orderSummary?.ticketSummary.length - 1 && (
                                        <div style={{ width: '100%', padding: '0rem 0.75rem' }}>
                                            <Divider sx={dividerStyles} />
                                        </div>
                                    )}
                                </Fragment>
                            ))}
                        </div>
                    </div>

                    <div className="purchased-ticket-breakup-container">
                        <BreakupAccordion>
                            <TicketBreakup
                                breakup={orderSummary?.cartSummary.subTotalWithTaxBreakup.ticketBreakup}
                                grossTotal={orderSummary?.cartSummary.subTotalWithTaxBreakup.grossTotal}
                            />
                        </BreakupAccordion>
                        <BreakupAccordion title={'Fees Breakup'}>
                            <div className="checkout-fees-breakup-container">

                                <div className="individual-fees-breakup-container">

                                    <Body1>
                                        Booking Fees Breakup
                                    </Body1>

                                    <FeesBreakup
                                        breakup={orderSummary?.cartSummary.subTotalWithTaxBreakup.ticketBreakup}
                                        grossTotal={orderSummary?.cartSummary.subTotalWithTaxBreakup.grossTotal}
                                        bookingFeesTaxBreakup={orderSummary?.cartSummary.bookingFees.taxBreakup}
                                        totalBookingFees={orderSummary?.cartSummary.bookingFees.totalBookingFees}
                                        bookingFeesInclusiveTax={orderSummary?.cartSummary.bookingFees.bookingFeesInclusiveTax}
                                        bookingFeesExclusiveTax={orderSummary?.cartSummary.bookingFees.bookingFeesExclusiveTax}
                                    />

                                    <div className="booking-fees-instructions">
                                        <Body2>Fees:</Body2>
                                        <Body3>
                                            1. ₹10 /- per ticket on tickets priced between ₹100 & ₹500.
                                        </Body3>
                                        <Body3>
                                            2. 2% per ticket on tickets priced less than ₹100 & greater than ₹500.
                                        </Body3>
                                    </div>
                                </div>

                                {orderSummary?.cartSummary.platformFees.platformFeesInclusiveTax > 0 && (
                                <div className="individual-fees-breakup-container">

                                    <Body1>
                                        Platform Fees Breakup
                                    </Body1>

                                    <PlatformFees
                                        breakup={orderSummary?.cartSummary.subTotalWithTaxBreakup.ticketBreakup}
                                        platformFeesTaxBreakup={orderSummary?.cartSummary.platformFees.taxBreakup}
                                        platformFeesExclusiveTax={orderSummary?.cartSummary.platformFees.platformFeesExclusiveTax}
                                        platformFeesInclusiveTax={orderSummary?.cartSummary.platformFees.platformFeesInclusiveTax}
                                    />

                                    <div className="booking-fees-instructions">
                                        <Body2>Fees:</Body2>
                                        <Body3>
                                            {/* 1. 3% per ticket on tickets priced less than ₹100. */}
                                            1. Commission on total gross sale: 3%.
                                        </Body3>
                                        <Body3>
                                            {/* 2. 3% + ₹8 /- per ticket on tickets priced ₹100 or more. */}
                                            2. Service fee on all tickets price ₹100 or more: ₹8/-
                                        </Body3>
                                    </div>

                                    <div className="booking-fees-instructions">
                                        <Body2>Note:</Body2>
                                        <Body3>
                                            Total quantity of tickets price ₹100 or above: 50.
                                        </Body3>
                                    </div>

                                </div>
                                )}

                            </div>
                        </BreakupAccordion>
                    </div>
                </div>

            </div>

            <div className="event-ticket-purchase-right-container">

                <OrderSummaryCart
                    totalTicketsQty={orderSummary?.cartSummary.totalTicketsQty}
                    subTotalWithoutTax={orderSummary?.cartSummary.subTotalWithoutTax}
                    totalTaxAmount={orderSummary?.cartSummary.taxAmount}
                    totalTaxAmountBreakup={orderSummary?.cartSummary.taxAmountBreakup}
                    totalPayableAmount={orderSummary?.cartSummary.totalPayableAmount}
                    subTotalWithTax={orderSummary?.cartSummary.subTotalWithTax}
                    fees={orderSummary?.cartSummary.fees}
                    toggleTaxBreakup={() => setShowTaxBreakup(!showTaxBreakup)}
                    showTaxBreakup={showTaxBreakup}
                />

                <Button
                    variant="main"
                    fullWidth
                    disabled={loading || openIframe}
                    onClick={initiatePaymentProcess}
                >
                    Proceed to payment {loading && !openModal && (
                        <CircularProgress
                            size={20}
                            sx={{
                                marginLeft: '0.75rem',
                                color: 'var(--primary-color)'
                            }}
                        />
                    )}
                </Button>

            </div>

            <Dialog
                open={openIframe}
                onClose={() => setOpenIFrame(false)}
                fullWidth
                sx={{
                    '& .MuiDialogContent-root': {
                        background: 'transparent',
                        height: '100%',
                        padding: '0',
                        overflowY: 'hidden'
                    },
                    '& .MuiPaper-root': {
                        background: 'var(--primary-color)',
                        borderRadius: '0',
                        height: '100%',
                        maxHeight: 'max-content',
                        maxWidth: '80%',
                    }
                }}
            >
                <DialogContent>
                    <iframe
                        src={phonepeUrl}
                        title="Payment"
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                    />
                </DialogContent>
            </Dialog>

            <AlertDialog
                dialogId="event-cancelled-dialog"
                dialogTitle={modalVariant === "eventCancelled"
                    ? "Oops! Event Cancelled"
                    : "Oops! Event Drafted"
                }
                open={openModal}
                onConfirm={modalVariant === "eventCancelled" ? goToEventPage : onModalContinue}
                onCancel={goToEventPage}
                primaryBtnName={modalVariant === "eventCancelled" ? "Go to Homepage" : "Continue"}
                secondaryBtnName='Cancel'
                shouldShowSecondaryBtn={modalVariant === "eventDrafted"}
                loading={loading && openModal}
                disabled={loading && openModal}
            >
                {modalVariant === "eventCancelled" && (
                    <Body1>
                        We're sorry, but this event has been cancelled and is no longer available for ticket purchases. Please visit our homepage to explore other events.
                    </Body1>
                )}

                {modalVariant === "eventDrafted" && (
                    <Body1>
                        We're sorry, but this event is currently in draft status and ticket purchases are temporarily unavailable. Wait for the event organizer to publish it again. Meanwhile, feel free to explore other events on our homepage.
                    </Body1>
                )}

            </AlertDialog>
        </Fragment>
    )
}

function BreakupAccordion({ title = 'Tickets Breakup', children }) {
    return (
        <Accordion
            sx={{
                alignSelf: 'stretch',
                fontWeight: 700,
                // background: 'var(--gradient-blue)',
                background: 'transparent',
                color: 'var(--quaternary-color)',
                borderRadius: '1rem !important',
                boxShadow: 'none',
                textAlign: 'justify',
                '& .MuiAccordionSummary-root': {
                    minHeight: '56px'
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                    color: 'var(--quaternary-color)'
                },
                '& .MuiAccordionDetails-root': {
                    padding: '0 0.75rem',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                    padding: '0'
                }}
            >
                <H5>
                    {title}
                </H5>
            </AccordionSummary>
            <AccordionDetails>
                {children}
            </AccordionDetails>
        </Accordion>
    )
}

function OrderSummaryCart({
    totalTicketsQty,
    subTotalWithoutTax,
    totalTaxAmount,
    totalTaxAmountBreakup,
    totalPayableAmount,
    subTotalWithTax,
    fees,
    showTaxBreakup,
    toggleTaxBreakup = () => { }
}) {
    return (
        <div className="event-ticket-order-summary-cart">

            <H6 color="var(--secondary-color)">Order Summary</H6>

            <Divider sx={dividerStyles} />

            <div className="ticket-order-calculation-container">
                <div className="ticket-order-summary-row">
                    <div className="ticket-order-summary-row-title">
                        <Body1>Total Quantity</Body1>
                    </div>
                    <div className="ticket-order-summary-row-amount">
                        <Body1 color="var(--secondary-color)">
                            {totalTicketsQty}
                        </Body1>
                    </div>
                </div>

                {/* ticket subtotal section */}
                <div className="ticket-order-summary-row">
                    <div className="ticket-order-summary-row-title">
                        <Body1>Sub Total</Body1>
                    </div>
                    <div className="ticket-order-summary-row-amount">
                        <Body1 color="var(--secondary-color)">
                            {ccyFormat(subTotalWithoutTax)}
                        </Body1>
                    </div>
                </div>

                {/* Taxes with breakup section */}
                {totalTaxAmount > 0 && (
                    <div className="ticket-taxes-with-breakup-container">
                        <div className="ticket-taxes-row">
                            <div className="ticket-taxes-title">
                                <Body1>Taxes</Body1>
                            </div>
                            <div className="ticket-taxes-amount">
                                <Body1 color="var(--secondary-color)">
                                    {ccyFormat(totalTaxAmount)}
                                </Body1>
                            </div>
                        </div>
                        <div className="show-order-breakup" onClick={toggleTaxBreakup}>
                            <Body3 style={{ cursor: 'pointer' }}>Show Breakup</Body3>
                        </div>

                        {showTaxBreakup && (
                            <div className="ticket-taxes-breakup-container">
                                {Object.entries(totalTaxAmountBreakup || {}).map(([key, amount]) => {
                                    // Only show tax entries with value > 0
                                    if (Number(amount) > 0) {
                                        const label = key.replace(/amount$/i, '').toUpperCase();
                                        return (
                                            <div
                                                key={key}
                                                className="ticket-taxes-breakup-additional-row"
                                            >
                                                <div className="ticket-taxes-breakup-title">
                                                    <Body3>{label}</Body3>
                                                </div>
                                                <div className="ticket-taxes-breakup-amount">
                                                    <Body3>{ccyFormat(Number(amount))}</Body3>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        )}

                    </div>
                )}

                {totalTaxAmount > 0 && (
                    <div className="ticket-order-summary-row">
                        <div className="ticket-order-summary-row-title">
                            <Body1>Total</Body1>
                        </div>
                        <div className="ticket-order-summary-row-amount">
                            <Body1 color="var(--secondary-color)">
                                {ccyFormat(subTotalWithTax)}
                            </Body1>
                        </div>
                    </div>
                )}

                {/* booking fees with breakup section */}
                <div className="ticket-taxes-with-breakup-container">
                    <div className="ticket-taxes-row">
                        <div className="ticket-taxes-title">
                            <Body1>Fees</Body1>
                        </div>
                        <div className="ticket-taxes-amount">
                            <Body1 color="var(--secondary-color)">{ccyFormat(fees)}</Body1>
                        </div>
                    </div>
                </div>

            </div>

            <div className="total-ticket-order-qty">
                <H6>Payable Amount :</H6>
                <H6 color="var(--secondary-color)">{ccyFormat(totalPayableAmount)}</H6>
            </div>

            <Divider sx={dividerStyles} />

            <div className="event-ticket-order-tnc">

                <div className="event-ticket-order-tnc-checkbox">
                    <Checkbox
                        sx={tncCheckboxStyle}
                        size="small"
                    />
                </div>

                <div className="event-ticket-order-tnc-text">
                    <Body3>
                        By proceeding, I agree to the Event's <a href="#" className="order-summary-tnc-link">Terms and Conditions</a> and <a href="#" className="order-summary-tnc-link">Privacy Policy</a>.
                    </Body3>
                </div>
            </div>

        </div>
    )
}

function TicketBreakup({ breakup = [], grossTotal = null }) {
    return (
        <TableContainer sx={tableStyles.container}>
            <Table stickyHeader aria-label="sticky table" sx={tableStyles.table}>
                <TableHead sx={tableStyles.dataCell}>
                    <TableRow sx={tableStyles.dataCell}>
                        <TableCell
                            align="left"
                            sx={tableHeaderStyles.ticketName}
                        >
                            <H6>Ticket Name</H6>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableHeaderStyles.price}
                        >
                            <H6>Price</H6>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableHeaderStyles.quantity}
                        >
                            <H6>Qty</H6>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableHeaderStyles.total}
                        >
                            <H6>GST</H6>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableHeaderStyles.gst}
                        >
                            <H6>GST Value</H6>
                        </TableCell>
                        <TableCell align="center" sx={tableHeaderStyles.subTotal}>
                            <H6>Sub Total</H6>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {breakup.map((row, index) => (
                        <TableRow
                            hover
                            key={index}
                            role="checkbox"
                            tabIndex={-1}
                            sx={{ borderBottom: '1px solid var(--divider-color)' }}
                        >
                            <TableCell
                                align="left"
                                sx={tableBodyStyles.ticketName}
                            >
                                <Body1>
                                    {row.ticketName}
                                </Body1>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.price}
                                width="120px"
                            >
                                <Body2>
                                    {ccyFormat(row.price)}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.quantity}
                                width="80px"
                            >
                                <Body2>
                                    {row.quantity}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.gst}
                                width="80px"
                            >
                                <Body2>
                                    {row.gstRate === 0 ? '-' : row.gstRate + '%'}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.price}
                                width="130px"
                            >
                                <Body2>
                                    {ccyFormat(row.gstValueOnTicketPrice)}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.subTotal}
                                width="150px"
                            >
                                <Body2>
                                    {ccyFormat(row.totalAmountWithGST)}
                                </Body2>
                            </TableCell>
                        </TableRow>
                    ))}

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell
                            align="right"
                            colSpan={5}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                                borderRight: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                Gross Total
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                {ccyFormat(grossTotal)}
                            </Body2>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    )
}


function FeesBreakup({
    breakup = [],
    bookingFeesInclusiveTax = null,
    bookingFeesExclusiveTax = null,
    bookingFeesTaxBreakup = {},
}) {

    const {
        cgstRate,
        sgstRate,
        igstRate,
        utgstRate,
        cgstAmount,
        sgstAmount,
        utgstAmount,
        igstAmount
    } = bookingFeesTaxBreakup;

    // check if all values are zero
    const allZero =
        parseFloat(cgstRate) === 0 &&
        parseFloat(sgstRate) === 0 &&
        parseFloat(igstRate) === 0 &&
        parseFloat(utgstRate) === 0;

    if (allZero) return null;

    return (
        <TableContainer sx={tableStyles.container}>
            <Table stickyHeader aria-label="sticky table" sx={tableStyles.table}>
                <TableHead sx={tableStyles.dataCell}>
                    <TableRow sx={tableStyles.dataCell}>
                        {bookingFeesHeader.map((header, index) => (
                            <TableCell
                                key={index}
                                align={header.align}
                                sx={header.sx}
                            >
                                <H6>{header.name}</H6>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {breakup.map((row, index) => (
                        <TableRow
                            hover
                            key={index}
                            role="checkbox"
                            tabIndex={-1}
                            sx={{ borderBottom: '1px solid var(--divider-color)' }}
                        >
                            <TableCell
                                align="left"
                                sx={tableBodyStyles.ticketName}
                                width="354px"
                            >
                                <Body1>
                                    {row.ticketName}
                                </Body1>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.price}
                                width="120px"
                            >
                                <Body2>
                                    {ccyFormat(row.price)}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.quantity}
                                width="90px"
                            >
                                <Body2>
                                    {ccyFormat(row.bookingFee)}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.quantity}
                                width="90px"
                            >
                                <Body2>
                                    {row.quantity}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.subTotal}
                                width="150px"
                            >
                                <Body2>
                                    {/* {ccyFormat(row.totalAmountWithGST)} */}
                                    {ccyFormat(row.bookingTotal)}
                                </Body2>
                            </TableCell>
                        </TableRow>
                    ))}

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>


                    {/* Gross Total Exclusive Tax */}
                    <TableRow>
                        <TableCell
                            align="right"
                            colSpan={4}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                                borderRight: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                Booking Fees (Exclusive Tax)
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                {/* {ccyFormat(grossTotal)} */}
                                {ccyFormat(bookingFeesExclusiveTax)}
                            </Body2>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>


                    {/* Tax Header */}
                    <TableRow>
                        <TableCell
                            align="right"
                            colSpan={2}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: 'none',
                                borderTop: '1px solid var(--divider-color)',
                                borderRight: '1px solid var(--divider-color)',
                            }}
                        >
                        </TableCell>
                        <TableCell
                            align="center"
                            colSpan={3}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: 'none',
                                borderTop: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                Tax
                            </Body2>
                        </TableCell>
                    </TableRow>


                    {/* SGST */}
                    {parseFloat(sgstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={2}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>SGST</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(sgstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}


                    {/* IGST */}
                    {parseFloat(igstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={2}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                colSpan={2}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>IGST {igstRate}%</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(igstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}


                    {/* UTGST */}
                    {parseFloat(utgstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={2}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>UTGST</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(utgstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}


                    {/* CGST */}
                    {parseFloat(cgstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={2}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>CGST</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(cgstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}


                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>

                    {/* Booking Fees Inclusive Tax */}
                    <TableRow>
                        <TableCell
                            align="right"
                            colSpan={4}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                                borderRight: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                Booking Fees (Inclusive Tax)
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                {/* {ccyFormat(totalBookingFees)} */}
                                {ccyFormat(bookingFeesInclusiveTax)}
                            </Body2>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>

                </TableBody>
            </Table>
        </TableContainer>
    )
}


function PlatformFees({
    breakup = [],
    platformFeesExclusiveTax = null,
    platformFeesInclusiveTax = null,
    platformFeesTaxBreakup = {}
}) {

    const {
        cgstRate,
        sgstRate,
        igstRate,
        utgstRate,
        cgstAmount,
        sgstAmount,
        utgstAmount,
        igstAmount
    } = platformFeesTaxBreakup;

    // check if all values are zero
    const allZero =
        parseFloat(cgstRate) === 0 &&
        parseFloat(sgstRate) === 0 &&
        parseFloat(igstRate) === 0 &&
        parseFloat(utgstRate) === 0;

    if (allZero) return null;

    return (
        <TableContainer sx={tableStyles.container}>
            <Table stickyHeader aria-label="sticky table" sx={tableStyles.table}>
                <TableHead sx={tableStyles.dataCell}>
                    <TableRow sx={tableStyles.dataCell}>
                        {platformFeesHeaderNames.map((header, index) => (
                            <TableCell
                                key={index}
                                align={header.align}
                                sx={header.sx}
                                colSpan={header.name === 'Total' ? 2 : 1}
                            >
                                <H6>{header.name}</H6>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* 
                    {breakup.map((row, index) => (
                        <TableRow
                            hover
                            key={index}
                            role="checkbox"
                            tabIndex={-1}
                            sx={{ borderBottom: '1px solid var(--divider-color)' }}
                        >
                            <TableCell
                                align="left"
                                sx={tableBodyStyles.ticketName}
                            >
                                <Body1>
                                    {row.ticketName}
                                </Body1>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.price}
                                width="120px"
                            >
                                <Body2>
                                    {ccyFormat(row.price)}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.price}
                                width="90px"
                            >
                                <Body2>
                                    {row.quantity}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.price}
                                width="130px"
                            >
                                <Body2>
                                    {ccyFormat(row.percentageCharges)}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.price}
                                width="130px"
                            >
                                <Body2>
                                    {ccyFormat(row.fixedRupeeCharges)}
                                </Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={tableBodyStyles.subTotal}
                                width="120px"
                                colSpan={2}
                            >
                                <Body2>
                                    {ccyFormat(row.totalCharges)}
                                </Body2>
                            </TableCell>
                        </TableRow>
                    ))}
                         */}

                    <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        sx={{ borderBottom: '1px solid var(--divider-color)' }}
                    >
                        <TableCell
                            align="left"
                            sx={tableBodyStyles.ticketName}
                        >
                            <Body1>
                                {/* {row.ticketName} */}
                                Platform Commission
                            </Body1>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.price}
                            width="120px"
                        >
                            <Body2>
                                {/* {ccyFormat(row.price)} */}
                                3%
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.price}
                            width="90px"
                        >
                            <Body2>
                                {/* {row.quantity} */}
                                5
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.price}
                            width="130px"
                        >
                            <Body2>
                                {/* {ccyFormat(row.percentageCharges)} */}
                                {ccyFormat(1245)}
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.subTotal}
                            width="120px"
                            colSpan={2}
                        >
                            <Body2>
                                {/* {ccyFormat(row.totalCharges)} */}
                                {ccyFormat(12541)}
                            </Body2>
                        </TableCell>
                    </TableRow>

                    <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        sx={{ borderBottom: '1px solid var(--divider-color)' }}
                    >
                        <TableCell
                            align="left"
                            sx={tableBodyStyles.description}
                        >
                            <Body1>
                                {/* {row.ticketName} */}
                                Service Fee
                            </Body1>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.price}
                            width="120px"
                        >
                            <Body2>
                                {/* {ccyFormat(row.price)} */}
                                {ccyFormat(8)}
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.price}
                            width="90px"
                        >
                            <Body2>
                                {/* {row.quantity} */}
                                5
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.grossTicketPrice}
                        // width="130px"
                        >
                            <Body2>
                                {/* {ccyFormat(row.percentageCharges)} */}
                                {ccyFormat(1245)}
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={tableBodyStyles.subTotal}
                            width="120px"
                            colSpan={2}
                        >
                            <Body2>
                                {/* {ccyFormat(row.totalCharges)} */}
                                {ccyFormat(12541)}
                            </Body2>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>


                    {/* Platform Fees Exclusive Tax */}
                    <TableRow>
                        <TableCell
                            align="right"
                            colSpan={5}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                                borderRight: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                {/* Platform Fees (Exclusive Tax) */}
                                Gross Total
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                {ccyFormat(platformFeesExclusiveTax)}
                            </Body2>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>


                    {/* Tax Header */}
                    <TableRow>
                        <TableCell
                            align="right"
                            colSpan={3}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: 'none',
                                borderTop: '1px solid var(--divider-color)',
                                borderRight: '1px solid var(--divider-color)',
                            }}
                        >
                        </TableCell>
                        <TableCell
                            align="center"
                            colSpan={3}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: 'none',
                                borderTop: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                Tax
                            </Body2>
                        </TableCell>
                    </TableRow>

                    {/* SGST */}
                    {parseFloat(sgstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={3}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                colSpan={2}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>SGST</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(sgstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}


                    {/* IGST */}
                    {parseFloat(igstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={3}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                colSpan={2}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>IGST {igstRate}%</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(igstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}


                    {/* UTGST */}
                    {parseFloat(utgstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={3}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>UTGST</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(utgstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}


                    {/* CGST */}
                    {parseFloat(cgstRate) > 0 && (
                        <TableRow>
                            <TableCell
                                align="right"
                                colSpan={3}
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            />
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "1px solid var(--divider-color)",
                                    borderTop: "1px solid var(--divider-color)",
                                    borderRight: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>CGST</Body2>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    ...tableBodyStyles.subTotal,
                                    borderBottom: "none",
                                    borderTop: "1px solid var(--divider-color)",
                                }}
                            >
                                <Body2>{ccyFormat(cgstAmount)}</Body2>
                            </TableCell>
                        </TableRow>
                    )}

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>


                    {/* Platform Fees Inclusive Tax */}
                    <TableRow>
                        <TableCell
                            align="right"
                            colSpan={5}
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                                borderRight: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                Platform Fees (Inclusive Tax)
                            </Body2>
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                ...tableBodyStyles.subTotal,
                                borderBottom: '1px solid var(--divider-color)',
                                borderTop: '1px solid var(--divider-color)',
                            }}
                        >
                            <Body2>
                                {ccyFormat(platformFeesInclusiveTax)}
                            </Body2>
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell colSpan={6} sx={tableStyles.borderNone}></TableCell>
                    </TableRow>

                </TableBody>
            </Table>
        </TableContainer>
    )
}

const tncCheckboxStyle = {
    color: 'var(--quaternary-color)',
    padding: '0',
    '&.Mui-checked': {
        color: 'var(--secondary-color)'
    }
}

const tableStyles = {
    container: {
        borderTopRightRadius: '1rem',
        borderTopLeftRadius: '1rem',
        maxHeight: '70vh',
    },
    table: {
        border: '1px solid var(--divider-color)',
        overflow: 'hidden',
        borderRadius: '1rem',
    },
    headerCell: {
        backgroundColor: '#283853',
        borderBottom: '1px solid var(--divider-color)',
    },
    dataCell: {
        borderBottom: '1px solid var(--divider-color)',
    },
    borderNone: {
        border: 'none',
    },
};

const dividerStyles = {
    borderColor: 'var(--divider-color)',
    alignSelf: 'stretch',
    // margin: '0rem 0.75rem'
};

// Table Headers Style Variables
const tableHeaderStyles = {
    ticketName: {
        ...tableStyles.headerCell,
        borderRight: '1px solid var(--divider-color)',
        maxWidth: '354px', // To prevent text overflow
    },
    quantity: {
        ...tableStyles.headerCell,
        borderRight: '1px solid var(--divider-color)',
    },
    price: {
        ...tableStyles.headerCell,
        borderRight: '1px solid var(--divider-color)',
    },
    total: {
        ...tableStyles.headerCell,
        borderRight: '1px solid var(--divider-color)',
    },
    gst: {
        ...tableStyles.headerCell,
        borderRight: '1px solid var(--divider-color)',
    },
    subTotal: {
        ...tableStyles.headerCell,
    },
};

// Table Body Styles Variables
const tableBodyStyles = {
    ticketName: {
        ...tableStyles.dataCell,
        borderRight: '1px solid var(--divider-color)',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: '354px'
    },
    quantity: {
        ...tableStyles.dataCell,
        borderRight: '1px solid var(--divider-color)',
    },
    price: {
        ...tableStyles.dataCell,
        borderRight: '1px solid var(--divider-color)',
    },
    total: {
        ...tableStyles.dataCell,
        borderRight: '1px solid var(--divider-color)',
    },
    gst: {
        ...tableStyles.dataCell,
        borderRight: '1px solid var(--divider-color)',
    },
    subTotal: {
        ...tableStyles.dataCell,
    },
    grossTicketPrice: {
        ...tableStyles.dataCell,
        borderRight: '1px solid var(--divider-color)',
        width: '200px'
    },
    description: {
        ...tableStyles.dataCell,
        borderRight: '1px solid var(--divider-color)',
        width: 'auto'
    }
};

const platformFeesHeaderNames = [
    { name: 'Description', align: 'left', sx: tableHeaderStyles.ticketName },
    { name: 'Charges', align: 'center', sx: tableHeaderStyles.price },
    { name: 'Qty', align: 'center', sx: tableHeaderStyles.quantity },
    { name: 'Gross Ticket Price', align: 'center', sx: tableHeaderStyles.price },
    // { name: 'Charges ₹', align: 'center', sx: tableHeaderStyles.price },
    { name: 'Total', align: 'center', sx: tableHeaderStyles.total },
];

const bookingFeesHeader = [
    { name: 'Ticket Name', align: 'left', sx: tableHeaderStyles.ticketName },
    { name: 'Price', align: 'center', sx: tableHeaderStyles.price },
    { name: 'Charges', align: 'center', sx: tableHeaderStyles.price },
    { name: 'Qty', align: 'center', sx: tableHeaderStyles.quantity },
    { name: 'Total', align: 'center', sx: tableHeaderStyles.total },
]